/**
 * Variant Form Component
 * Form for creating and editing product type variants with multiple rate types
 */

import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

const VariantForm = ({ 
  variant = null, 
  onSubmit, 
  onClose 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    minimumRentalDuration: '',
    rates: [{ rate: '', rateType: 'HOURLY' }]
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const rateTypeOptions = [
    { value: 'HOURLY', label: 'Hourly' },
    { value: 'DAILY', label: 'Daily' },
    { value: 'WEEKLY', label: 'Weekly' }
  ];

  // Initialize form with variant data if editing
  useEffect(() => {
    if (variant) {
      setFormData({
        name: variant.name || '',
        minimumRentalDuration: variant.minimumRentalDuration ? variant.minimumRentalDuration.toString() : '',
        rates: variant.rates && variant.rates.length > 0
          ? variant.rates.map(rate => ({
              rate: rate.rate ? rate.rate.toString() : '',
              rateType: rate.rateType || 'HOURLY'
            }))
          : [{ rate: '', rateType: 'HOURLY' }]
      });
    }
  }, [variant]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRateChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      rates: prev.rates.map((rate, i) => 
        i === index ? { ...rate, [field]: value } : rate
      )
    }));
  };

  const addRate = () => {
    setFormData(prev => ({
      ...prev,
      rates: [...prev.rates, { rate: '', rateType: 'HOURLY' }]
    }));
  };

  const removeRate = (index) => {
    if (formData.rates.length > 1) {
      setFormData(prev => ({
        ...prev,
        rates: prev.rates.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    // Validate required fields
    if (!formData.name.trim()) {
      alert('Please enter a variant name');
      return;
    }
    
    // Validate that at least one rate has a value
    const validRates = formData.rates.filter(rate => 
      rate.rate && parseFloat(rate.rate) > 0
    );
    
    if (validRates.length === 0) {
      alert('Please enter at least one valid rate');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Process the rates to ensure they're properly formatted
      const processedData = {
        name: formData.name,
        minimumRentalDuration: formData.minimumRentalDuration ? parseFloat(formData.minimumRentalDuration) : null,
        rates: validRates.map(rate => ({
          rate: parseFloat(rate.rate),
          rateType: rate.rateType
        }))
      };

      await onSubmit(processedData);
    } catch (error) {
      console.error('Variant form submission error:', error);
      alert('Failed to save variant. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-60 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        
        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {variant ? 'Edit Variant' : 'Create New Variant'}
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Variant Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Variant Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter variant name (e.g., Standard, Premium, etc.)"
                    required
                  />
                </div>

                {/* Minimum Rental Duration (Optional Override) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Minimum Rental Duration (hours)
                  </label>
                  <input
                    type="number"
                    min="0.5"
                    step="0.5"
                    value={formData.minimumRentalDuration}
                    onChange={(e) => handleInputChange('minimumRentalDuration', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Leave blank to use product type default"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Optional: Override the product type's minimum rental duration for this variant only
                  </p>
                </div>

                {/* Rates Section */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-md font-medium text-gray-900">Pricing Rates</h4>
                      <p className="text-sm text-gray-500">
                        Add different pricing options for this variant
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={addRate}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200"
                    >
                      <PlusIcon className="h-3 w-3 mr-1" />
                      Add Rate
                    </button>
                  </div>

                  <div className="space-y-3">
                    {formData.rates.map((rate, index) => (
                      <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Rate Amount
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <CurrencyDollarIcon className="h-4 w-4 text-gray-400" />
                            </div>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={rate.rate}
                              onChange={(e) => handleRateChange(index, 'rate', e.target.value)}
                              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                              placeholder="0.00"
                            />
                          </div>
                        </div>

                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Rate Type
                          </label>
                          <select
                            value={rate.rateType}
                            onChange={(e) => handleRateChange(index, 'rateType', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                          >
                            {rateTypeOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {formData.rates.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeRate(index)}
                            className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            title="Remove rate"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  {formData.rates.length === 0 && (
                    <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                      <CurrencyDollarIcon className="mx-auto h-8 w-8 text-gray-400" />
                      <p className="mt-2 text-sm text-gray-500">No rates configured</p>
                      <button
                        type="button"
                        onClick={addRate}
                        className="mt-2 inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200"
                      >
                        <PlusIcon className="h-3 w-3 mr-1" />
                        Add First Rate
                      </button>
                    </div>
                  )}
                </div>

                {/* Rate Preview */}
                <div className="bg-blue-50 p-3 rounded-lg">
                  <h5 className="text-sm font-medium text-blue-900 mb-2">Rate Preview</h5>
                  <div className="text-sm text-blue-700">
                    {formData.rates
                      .filter(rate => rate.rate && parseFloat(rate.rate) > 0)
                      .map((rate, index) => {
                        const rateLabels = {
                          'HOURLY': '/hr',
                          'DAILY': '/day',
                          'WEEKLY': '/week'
                        };
                        
                        return (
                          <div key={index}>
                            ${parseFloat(rate.rate).toFixed(2)}{rateLabels[rate.rateType] || ''}
                          </div>
                        );
                      })
                    }
                    {formData.rates.filter(rate => rate.rate && parseFloat(rate.rate) > 0).length === 0 && (
                      <span className="text-gray-400 italic">No valid rates configured</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Saving...' : (variant ? 'Update Variant' : 'Create Variant')}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VariantForm;